import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ─── Timezone ─────────────────────────────────────────────────────────────
// Module-level display timezone. Set once by <TimezoneProvider> on app mount.
// All formatDate/formatDateTime/formatTime calls read from here so every date
// in the CRM renders in the configured timezone without threading a param
// through ~45 call sites.
let _displayTimezone = "Europe/Berlin";

export function setDisplayTimezone(tz: string) {
  _displayTimezone = tz;
}

export function getDisplayTimezone(): string {
  return _displayTimezone;
}

/**
 * Shift a Date so that getHours()/getMinutes() return values in the display
 * timezone instead of the browser-local timezone. Use this ONLY for UI
 * components (calendars, time pickers) that rely on Date.getHours() internally.
 * The shifted Date is NOT a real UTC date — never persist or send it to an API.
 */
export function toDisplayDate(date: Date): Date {
  const browserOffset = date.getTimezoneOffset(); // minutes, sign inverted
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: _displayTimezone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  const match = tzPart?.value?.match(/GMT([+-]?\d+)/);
  const displayOffsetHours = match ? parseInt(match[1], 10) : 1;
  const displayOffsetMinutes = -(displayOffsetHours * 60);
  const offsetMs = (browserOffset - displayOffsetMinutes) * 60 * 1000;
  return new Date(date.getTime() + offsetMs);
}

/**
 * Inverse of toDisplayDate. Takes a "display date" (where getHours() returns
 * display-timezone hours) and shifts it back to a real UTC-correct Date.
 * Use this when constructing a Date via setHours() with a display-TZ hour value.
 */
export function fromDisplayDate(date: Date): Date {
  const browserOffset = date.getTimezoneOffset();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: _displayTimezone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  const match = tzPart?.value?.match(/GMT([+-]?\d+)/);
  const displayOffsetHours = match ? parseInt(match[1], 10) : 1;
  const displayOffsetMinutes = -(displayOffsetHours * 60);
  const offsetMs = (browserOffset - displayOffsetMinutes) * 60 * 1000;
  return new Date(date.getTime() - offsetMs);
}

/**
 * Format a date as a long, human-readable string ("12. März 2026").
 * Uses the app-wide display timezone.
 */
export function formatLongDate(dateString: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return date.toLocaleDateString('de-DE', {
    timeZone: _displayTimezone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  });
}

/**
 * Format a date as a relative human-readable string ("vor 2 Monaten").
 * Returns "" for invalid input. Uses German locale.
 */
export function formatRelative(dateString: string | Date): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = date.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  // Pick the largest fitting unit so the output reads naturally.
  // RelativeTimeFormat picks the right preposition ("vor" / "in") via sign.
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  const rtf = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });
  if (absMs < minute) return rtf.format(0, 'second'); // "jetzt"
  if (absMs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
  if (absMs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (absMs < week) return rtf.format(Math.round(diffMs / day), 'day');
  if (absMs < month) return rtf.format(Math.round(diffMs / week), 'week');
  if (absMs < year) return rtf.format(Math.round(diffMs / month), 'month');
  return rtf.format(Math.round(diffMs / year), 'year');
}

/**
 * Combined absolute + relative date — "12. März 2026 · vor 2 Monaten".
 * Use for surfaces where the user benefits from both the calendar date
 * (precise, navigable) and the human-feel relative anchor (recency at a
 * glance). The middle dot separator matches the activity-timeline header
 * style so the formatting reads consistently across the lead page.
 */
export function formatLongAndRelative(dateString: string | Date): string {
  if (!dateString) return '';
  const long = formatLongDate(dateString);
  const rel = formatRelative(dateString);
  return rel ? `${long} · ${rel}` : long;
}

/**
 * Format a date string to German locale format (dd.MM.yyyy).
 * Uses the app-wide display timezone (set via TimezoneProvider).
 */
export function formatDate(dateString: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (!date || isNaN(date.getTime())) return '';
  return date.toLocaleDateString('de-DE', {
    timeZone: _displayTimezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
}

/**
 * Format a date+time string to German locale (dd.MM.yyyy, HH:mm).
 * Uses the app-wide display timezone.
 */
export function formatDateTime(dateString: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (!date || isNaN(date.getTime())) return '';
  return date.toLocaleString('de-DE', {
    timeZone: _displayTimezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/**
 * Format a time string to German locale (HH:mm).
 * Uses the app-wide display timezone.
 */
export function formatTime(dateString: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (!date || isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('de-DE', {
    timeZone: _displayTimezone,
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/**
 * Format a number as currency in EUR (German locale)
 * Value is expected to be in cents - divide by 100 for display
 */
export function formatCurrency(valueInCents: number, includeCents = true): string {
  const value = valueInCents / 100;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0,
  }).format(value);
}

/**
 * Format a number as currency in EUR (German locale)
 * Value is expected to be in EUROS (not cents) - for Teleson API values
 */
export function formatEuro(valueInEuros: number, includeCents = true): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0,
  }).format(valueInEuros);
}
